import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import compact from 'lodash/compact';

import { actions as patientChannelsForShare } from '../../../../../../redux/healthboard/patientPortal/patient-channels-for-share';
import { actions as socialChannelsForShare } from '../../../../../../redux/healthboard/patientPortal/social-channels-for-share';
import { actions as loggingActionRequest } from '../../../../../../redux/healthboard/patientPortal/logging-action-req';
import { shareToChatModalActions } from '../../../../../../redux/healthboard/patientPortal/share-to-chat-modal';
import { shareToEmailModalActions } from '../../../../../../redux/healthboard/patientPortal/share-to-email-modal';
import { ALLOW_SOCIAL_CHAT_CREATION } from '../../../../../../constants/chat';
import { IDS } from '../../../../../../constants/ids';
import {
  ACTIVITY_LOG_USER_ACTION_TYPE,
  HEALTH_RECORD_DOC_TYPE,
} from '../../../../constants';
import {
  loggedUserIdSelector,
  targetUserIdSelector,
} from '../../../../../../redux/context/selectors';
import {
  downloadFile,
  getSafeUserId,
  parseFileAttribute,
} from '../../../utils';

const { PDF, ZIP } = HEALTH_RECORD_DOC_TYPE;
const { DOWNLOAD, VIEW } = ACTIVITY_LOG_USER_ACTION_TYPE;

export function useMenuItemBuilder(formatMessage, entity) {
  const { documentUid, title, files } = entity;

  const {
    zipFileData,
    pdfFileData,
    xmlFileData,
    isXmlFormat,
    isTxtFormat,
    isPdfFormat,
    txtFileData,
    anyTypeFileData,
  } = parseFileAttribute(files);

  const mainUserId = useSelector(loggedUserIdSelector);
  const targetUserId = useSelector(targetUserIdSelector);

  const { safeUserId } = getSafeUserId(targetUserId, mainUserId);

  const dispatch = useDispatch();

  const safeEntityCCD = isXmlFormat ? xmlFileData : txtFileData;
  const safeTitleOpenItems = isXmlFormat ? 'CCD' : 'TXT';
  const isConvertibleToPdfData =
    !isXmlFormat && !isTxtFormat && isPdfFormat && files && files.length >= 1;

  const fileType =
    get(files.find(file => file.extension !== PDF.fileType), 'extension', '') ||
    '';
  const getFileTypeWithoutPdf = fileType.toUpperCase() || 'CCD';

  const loggingEvent = eventType => {
    dispatch(
      loggingActionRequest.send({
        userId: safeUserId,
        actionType: eventType,
        documentUid,
      }),
    );
  };

  const {
    openAsCCD,
    openAsPDF,
    downloadAsCCD,
    downloadAsPDF,
    shareToChat,
    shareToEmail,
    downloadAsZIP,
  } = {
    openAsCCD: {
      id: IDS.PATIENT_PORTAL.TAB_HEALTH_RECORD.OPEN_AS_CCD,
      text: formatMessage(
        {
          id: `patient.portal.healthRecord.openAs`,
        },
        {
          fileType: isConvertibleToPdfData
            ? getFileTypeWithoutPdf
            : safeTitleOpenItems,
        },
      ),
      onClick: () => {
        loggingEvent(VIEW);
        window.open(safeEntityCCD.url, '_blank');
      },
    },
    openAsPDF: {
      id: IDS.PATIENT_PORTAL.TAB_HEALTH_RECORD.OPEN_AS_PDF,
      text: formatMessage({ id: 'patient.portal.healthRecord.openAsPDF' }),
      onClick: () => {
        window.open(pdfFileData.url, '_blank');
        loggingEvent(VIEW);
      },
    },
    downloadAsCCD: {
      id: IDS.PATIENT_PORTAL.TAB_HEALTH_RECORD.DOWNLOAD_AS_CCD,
      text: formatMessage(
        {
          id: `patient.portal.healthRecord.downloadAs`,
        },
        {
          fileType: isConvertibleToPdfData
            ? getFileTypeWithoutPdf
            : safeTitleOpenItems,
        },
      ),
      onClick: () => {
        downloadFile(
          safeEntityCCD.url,
          safeEntityCCD.extension,
          title,
          safeEntityCCD.mimeType,
        );
        loggingEvent(DOWNLOAD);
      },
    },
    downloadAsPDF: {
      id: IDS.PATIENT_PORTAL.TAB_HEALTH_RECORD.DOWNLOAD_AS_PDF,
      text: formatMessage({ id: 'patient.portal.healthRecord.downloadAsPDF' }),
      onClick: () => {
        downloadFile(pdfFileData.url, PDF.fileType, title, PDF.mimeType);
        loggingEvent(DOWNLOAD);
      },
    },
    downloadAsZIP: {
      id: IDS.PATIENT_PORTAL.TAB_HEALTH_RECORD.DOWNLOAD_AS_ZIP,
      text: formatMessage({ id: 'patient.portal.healthRecord.downloadAsCCD' }),
      onClick: () => {
        downloadFile(zipFileData.url, ZIP.fileType, title, ZIP.mimeType);
        loggingEvent(DOWNLOAD);
      },
    },
    shareToChat: {
      id: IDS.PATIENT_PORTAL.TAB_HEALTH_RECORD.SHARE_TO_CHAT,
      text: formatMessage({ id: 'patient.portal.healthRecord.shareToChat' }),
      onClick: () => {
        dispatch(
          patientChannelsForShare.send({
            type: 'patient',
            offset: 0,
            limit: 1000,
          }),
        );
        if (ALLOW_SOCIAL_CHAT_CREATION) {
          dispatch(
            socialChannelsForShare.send({
              type: 'social',
              offset: 0,
              limit: 1000,
            }),
          );
        }
        dispatch(shareToChatModalActions.open({ documentUid }));
      },
    },
    shareToEmail: {
      id: IDS.PATIENT_PORTAL.TAB_HEALTH_RECORD.SHARE_TO_EMAIL,
      text: formatMessage({ id: 'patient.portal.healthRecord.shareToEmail' }),
      onClick: () => {
        dispatch(shareToEmailModalActions.open({ documentUid }));
      },
    },
  };

  const withCCD = files.find(({ extension }) =>
    ['xml', 'txt'].includes(extension),
  );

  const withPDF = files.find(({ extension }) =>
    ['pdf'].includes(extension),
  );

  const withZIP = files.find(({ extension }) =>
    ['zip'].includes(extension),
  );

  const downloadZIP = withZIP && downloadAsZIP
  const downloadCCD = withCCD && downloadAsCCD

  const shareItems = [shareToChat, shareToEmail];
  const openItems = compact([withCCD && openAsCCD, withPDF && openAsPDF]);
  const downloadItems = compact([downloadZIP || downloadCCD, withPDF && downloadAsPDF]);

  return useMemo(
    () => ({
      openItems,
      downloadItems,
      shareItems,
      isXmlFormat,
      isTxtFormat,
      txtFileData,
      anyTypeFileData,
      isConvertibleToPdfData,
      loggingEvent,
    }),
    [entity],
  );
}



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/PatientPortal/DataList/Card/HealthRecordCard/useMenuItemBuilder.jsx